import { useGridApiContext } from '@mui/x-data-grid';
import PropTypes from 'prop-types';
import React, { useCallback } from 'react';
import OnboardingDatePicker from '../datePickers/OnboardingDatePicker';
import FingoGridDatePicker from '../datePickers/FingoGridDatePicker';
import dayjs from '../../config/dayjs';
import { useCartDateDisableCheck } from '../../hooks/useUpcomingHolidays';

const GridEditDateCell = ({
  field,
  value,
  row,
  onChange,
  onChangeAll,
  debtorAveragePaymentDays,
  minDate,
  maxDate,
  disabled,
  useOnboardingDatePicker,
  ButtonComponent,
}) => {
  const apiRef = useGridApiContext();
  const shouldDisableDate = useCartDateDisableCheck();

  const changeRowValue = useCallback(
    (changedRow, newValue) => {
      apiRef.current.setEditCellValue({
        id: changedRow.id,
        field,
        value: newValue,
      });
      apiRef.current.commitCellChange({ id: changedRow.id, field });
      apiRef.current.setCellMode(changedRow.id, field, 'view');
      onChange(changedRow, newValue);
    },
    [apiRef, field, onChange],
  );

  const formatValue = useCallback((momentDate) => {
    if (momentDate) {
      return momentDate ? momentDate.format('DD/MM/YYYY') : '';
    }
    return '';
  }, []);

  const handleChange = useCallback(
    (newValue, ...params) => {
      const formatedValue = formatValue(newValue);
      const dateRegex = /^\d{2}\/\d{2}\/\d{4}$/;
      if (dateRegex.test(formatedValue)) {
        const minDateStartOfDay = minDate.startOf('day');
        const momentDate = dayjs(formatedValue, 'DD-MM-YYYY');
        if (
          momentDate.isValid()
          && !shouldDisableDate(momentDate)
          && minDateStartOfDay <= momentDate
          && momentDate <= maxDate
        ) {
          changeRowValue(row, newValue, ...params);
        }
      } else {
        changeRowValue(row, '', ...params);
      }
    },
    [changeRowValue, row, shouldDisableDate, minDate, maxDate, formatValue],
  );

  const closeDatePicker = useCallback(() => {
    apiRef.current.getCellElement(row.id, field).click();
  }, [apiRef]);

  const defaultOnChangeAll = useCallback(
    (newValue) => {
      const allRows = apiRef.current.getAllRowIds();
      allRows.forEach((rowId) => {
        const newRow = apiRef.current.getRow(rowId); // Obtener cada fila
        changeRowValue(newRow, newValue); // Cambiar el valor de cada fila
      });
      closeDatePicker();
    },
    [apiRef, changeRowValue],
  );

  return useOnboardingDatePicker ? (
    <OnboardingDatePicker
      value={value}
      onChange={handleChange}
      onChangeAll={onChangeAll
        ? () => onChangeAll(value)
        : () => defaultOnChangeAll(value)}
      debtorAveragePaymentDays={debtorAveragePaymentDays}
      minDate={minDate}
      maxDate={maxDate}
      disabled={disabled}
      shouldDisableWeekend={shouldDisableDate}
      ButtonComponent={ButtonComponent}
    />
  ) : (
    <FingoGridDatePicker
      value={value}
      onChange={handleChange}
      onChangeAll={onChangeAll
        ? () => onChangeAll(value)
        : () => defaultOnChangeAll(value)}
      debtorAveragePaymentDays={debtorAveragePaymentDays}
      minDate={minDate}
      maxDate={maxDate}
      disabled={disabled}
      closeDatePicker={closeDatePicker}
      shouldDisableWeekend={shouldDisableDate}
    />
  );
};

GridEditDateCell.propTypes = {
  debtorAveragePaymentDays: PropTypes.number,
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  field: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.instanceOf(dayjs), PropTypes.string]),
  row: PropTypes.shape({ id: PropTypes.string.isRequired }).isRequired,
  onChange: PropTypes.func.isRequired,
  onChangeAll: PropTypes.func,
  minDate: PropTypes.instanceOf(dayjs),
  maxDate: PropTypes.instanceOf(dayjs),
  disabled: PropTypes.bool,
  ButtonComponent: PropTypes.elementType,
  useOnboardingDatePicker: PropTypes.bool,
};

GridEditDateCell.defaultProps = {
  debtorAveragePaymentDays: null,
  value: '',
  onChangeAll: null,
  minDate: dayjs().subtract(180, 'days'),
  maxDate: dayjs().add(150, 'days'),
  disabled: false,
  useOnboardingDatePicker: false,
  ButtonComponent: null,
};

export default GridEditDateCell;
